import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon, Visibility as VisibilityIcon, Replay as RepublishIcon } from '@mui/icons-material';
import devicesApi, { type Device } from '../services/devicesApi';
import runtimeBlueprintsApi, { type ClarityDeploymentRow } from '../services/runtimeBlueprintsApi';
import { useAuth } from '../contexts/AuthContext';

type DriftStatus = 'match' | 'mismatch' | 'unknown';

function driftStatus(desiredHash?: string | null, appliedHash?: string | null): DriftStatus {
  const d = (desiredHash || '').trim();
  const a = (appliedHash || '').trim();
  if (!d || !a) return 'unknown';
  return d === a ? 'match' : 'mismatch';
}

function statusChip(status: DriftStatus) {
  if (status === 'match') return <Chip size="small" color="success" label="match" />;
  if (status === 'mismatch') return <Chip size="small" color="error" label="mismatch" />;
  return <Chip size="small" variant="outlined" label="unknown" />;
}

export default function Blueprints() {
  const { effectiveUser: user } = useAuth();
  const capabilities = Array.isArray((user as any)?.capabilities) ? (user as any).capabilities : [];
  const canWriteBlueprints = capabilities.includes('blueprints:write');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  const [edgeHealth, setEdgeHealth] = useState<any | null>(null);
  const [edgeBlueprints, setEdgeBlueprints] = useState<any | null>(null);
  const [drbStatus, setDrbStatus] = useState<any | null>(null);
  const [policiesDesired, setPoliciesDesired] = useState<ClarityDeploymentRow | null>(null);
  const [zonesDesiredByCamera, setZonesDesiredByCamera] = useState<Record<string, ClarityDeploymentRow>>({});

  const [viewOpen, setViewOpen] = useState(false);
  const [viewJson, setViewJson] = useState<any | null>(null);
  const [republishing, setRepublishing] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: '',
  });

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await devicesApi.listDevices();
      setDevices(res || []);
      if (!selectedDevice && (res || []).length) setSelectedDevice(res[0]);
    } catch (e: any) {
      setError(e?.message || 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [selectedDevice]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const refreshSelected = useCallback(async () => {
    if (!selectedDevice?.device_id) return;
    setLoading(true);
    setError(null);
    try {
      const deviceId = selectedDevice.device_id;
      // Default tier: fetch the diff-friendly runtime status export (small, UI-safe).
      // Forensics export is only fetched on-demand (View/Download).
      const [st, drb] = await Promise.all([runtimeBlueprintsApi.edgeRuntimeStatus(deviceId), runtimeBlueprintsApi.drbStatus(deviceId)]);
      // Keep existing state variable names to minimize churn in the UI rendering.
      setEdgeHealth(st);
      setEdgeBlueprints(st);
      setDrbStatus(drb);

      const polList = await runtimeBlueprintsApi.listClarityDeployments({ edge_device_id: deviceId, config_type: 'policies', limit: 1, offset: 0 });
      setPoliciesDesired((polList.deployments || [])[0] || null);

      const zonesList = await runtimeBlueprintsApi.listClarityDeployments({ edge_device_id: deviceId, config_type: 'zones', limit: 50, offset: 0 });
      const byCam: Record<string, ClarityDeploymentRow> = {};
      for (const row of zonesList.deployments || []) {
        const cam = (row.camera_id || '').trim();
        if (!cam) continue;
        if (!byCam[cam]) byCam[cam] = row;
      }
      setZonesDesiredByCamera(byCam);
    } catch (e: any) {
      setError(e?.message || 'Failed to load runtime blueprint status');
    } finally {
      setLoading(false);
    }
  }, [selectedDevice?.device_id]);

  useEffect(() => {
    if (selectedDevice?.device_id) refreshSelected();
  }, [selectedDevice?.device_id, refreshSelected]);

  const onDownloadRuntime = useCallback(async () => {
    if (!selectedDevice?.device_id) return;
    try {
      // Default export: status-only (small, diffable). Debug export is opt-in.
      const data = await runtimeBlueprintsApi.edgeBlueprints(selectedDevice.device_id, {
        includeContent: false,
        includeHashes: true,
        redactNetwork: true,
      });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `runtime_blueprints_${selectedDevice.device_id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setSnackbar({ open: true, severity: 'error', message: e?.message || 'Download failed' });
    }
  }, [selectedDevice?.device_id]);

  const onDownloadRuntimeDebug = useCallback(async () => {
    if (!selectedDevice?.device_id) return;
    try {
      const data = await runtimeBlueprintsApi.edgeBlueprints(selectedDevice.device_id, {
        includeContent: true,
        includeHashes: true,
        redactNetwork: true,
      });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `runtime_blueprints_debug_${selectedDevice.device_id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setSnackbar({ open: true, severity: 'error', message: e?.message || 'Download failed' });
    }
  }, [selectedDevice?.device_id]);

  const onDownloadPolicies = useCallback(async () => {
    if (!selectedDevice?.device_id) return;
    try {
      const data = await runtimeBlueprintsApi.edgeBlueprints(selectedDevice.device_id, true);
      const pol = data?.artifacts?.effective_policies?.content ?? data?.artifacts?.effective_policies ?? null;
      const blob = new Blob([JSON.stringify(pol || {}, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `effective_policies_${selectedDevice.device_id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setSnackbar({ open: true, severity: 'error', message: e?.message || 'Download failed' });
    }
  }, [selectedDevice?.device_id]);

  const onDownloadZones = useCallback(
    async (cameraId: string) => {
      if (!selectedDevice?.device_id || !cameraId) return;
      try {
        const data = await runtimeBlueprintsApi.edgeBlueprints(selectedDevice.device_id, true);
        const z = data?.artifacts?.effective_zones?.[cameraId]?.content ?? data?.artifacts?.effective_zones?.[cameraId] ?? null;
        const blob = new Blob([JSON.stringify(z || {}, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `effective_zones_${selectedDevice.device_id}_${cameraId}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e: any) {
        setSnackbar({ open: true, severity: 'error', message: e?.message || 'Download failed' });
      }
    },
    [selectedDevice?.device_id],
  );

  const onViewRuntime = useCallback(async () => {
    if (!selectedDevice?.device_id) return;
    setLoading(true);
    try {
      const data = await runtimeBlueprintsApi.edgeBlueprints(selectedDevice.device_id, {
        includeContent: false,
        includeHashes: true,
        redactNetwork: true,
      });
      setViewJson(data);
      setViewOpen(true);
    } catch (e: any) {
      setSnackbar({ open: true, severity: 'error', message: e?.message || 'View failed' });
    } finally {
      setLoading(false);
    }
  }, [selectedDevice?.device_id]);

  const onViewRuntimeDebug = useCallback(async () => {
    if (!selectedDevice?.device_id) return;
    setLoading(true);
    try {
      const data = await runtimeBlueprintsApi.edgeBlueprints(selectedDevice.device_id, {
        includeContent: true,
        includeHashes: true,
        redactNetwork: true,
      });
      setViewJson(data);
      setViewOpen(true);
    } catch (e: any) {
      setSnackbar({ open: true, severity: 'error', message: e?.message || 'View failed' });
    } finally {
      setLoading(false);
    }
  }, [selectedDevice?.device_id]);

  const appliedPolicies = edgeHealth?.config_versions?.policies || {};
  const appliedZones = edgeHealth?.config_versions?.zones || {};

  const policiesDrift = useMemo(() => {
    return driftStatus(policiesDesired?.bundle_hash || null, appliedPolicies?.bundle_hash || null);
  }, [policiesDesired?.bundle_hash, appliedPolicies?.bundle_hash]);

  const mainDrift = useMemo(() => {
    const desiredMain = drbStatus?.desired?.main_blueprint_hash || null;
    const appliedMain = drbStatus?.applied?.artifact_versions?.main_blueprint_hash || null;
    return driftStatus(desiredMain, appliedMain);
  }, [drbStatus]);

  const republishPolicies = useCallback(async () => {
    if (!selectedDevice?.device_id) return;
    setRepublishing(true);
    try {
      await runtimeBlueprintsApi.republishClarity({ edge_device_id: selectedDevice.device_id, config_type: 'policies' });
      setSnackbar({ open: true, severity: 'success', message: 'Republished policies (retained)' });
      await refreshSelected();
    } catch (e: any) {
      setSnackbar({ open: true, severity: 'error', message: e?.message || 'Republish failed' });
    } finally {
      setRepublishing(false);
    }
  }, [selectedDevice?.device_id, refreshSelected]);

  const republishZones = useCallback(
    async (cameraId: string) => {
      if (!selectedDevice?.device_id || !cameraId) return;
      setRepublishing(true);
      try {
        await runtimeBlueprintsApi.republishClarity({ edge_device_id: selectedDevice.device_id, config_type: 'zones', camera_id: cameraId });
        setSnackbar({ open: true, severity: 'success', message: `Republished zones for ${cameraId}` });
        await refreshSelected();
      } catch (e: any) {
        setSnackbar({ open: true, severity: 'error', message: e?.message || 'Republish failed' });
      } finally {
        setRepublishing(false);
      }
    },
    [selectedDevice?.device_id, refreshSelected],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Runtime Blueprints</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshSelected} disabled={loading || !selectedDevice?.device_id}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Alert severity="info">
          This page shows <b>device ↔ cloud sync</b> for runtime artifacts: DRB main blueprint hash, policies bundle, and zones bundles. Use it to
          detect drift and (if needed) republish the latest retained config to an edge.
        </Alert>
      </Paper>

      {!canWriteBlueprints && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only blueprint access. Download and view actions are allowed; republish actions are disabled for this account.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Device</Typography>
        <Autocomplete
          options={devices}
          value={selectedDevice}
          onChange={(_e, v) => setSelectedDevice(v)}
          getOptionLabel={(d) => `${d.device_id}${d.ip_address ? ` (${d.ip_address})` : ''}`}
          renderInput={(params) => <TextField {...params} placeholder="Select a device…" />}
        />
      </Paper>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ py: 1 }}>
                <Skeleton variant="text" width="50%" height={24} />
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Skeleton variant="rounded" width={80} height={24} />
                  <Skeleton variant="rounded" width={100} height={24} />
                  <Skeleton variant="rounded" width={100} height={24} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : !selectedDevice ? (
          <Typography color="text.secondary">Select a device to view runtime blueprints.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>DRB (main blueprint)</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {statusChip(mainDrift)}
                <Chip size="small" variant="outlined" label={`desired v${drbStatus?.desired?.bundle_version ?? '-'}`} />
                <Chip size="small" variant="outlined" label={`applied v${drbStatus?.applied?.applied_bundle_version ?? '-'}`} />
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  main_hash={(drbStatus?.applied?.artifact_versions?.main_blueprint_hash || drbStatus?.desired?.main_blueprint_hash || '').slice(0, 24) || '-'}
                </Typography>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Policies</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {statusChip(policiesDrift)}
                <Chip size="small" variant="outlined" label={`desired v${policiesDesired?.bundle_version ?? '-'}`} />
                <Chip size="small" variant="outlined" label={`applied v${appliedPolicies?.bundle_version ?? '-'}`} />
                <Tooltip title="Republish the latest retained policies payload to this edge">
                  <span>
                    <Button size="small" startIcon={<RepublishIcon />} onClick={republishPolicies} disabled={republishing || !canWriteBlueprints}>
                      Republish
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Download the edge's applied effective_policies.json">
                  <span>
                    <Button size="small" startIcon={<DownloadIcon />} onClick={onDownloadPolicies} disabled={!selectedDevice?.device_id}>
                      Download policies
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Zones (per camera)</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Camera</TableCell>
                    <TableCell>Desired</TableCell>
                    <TableCell>Applied</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(appliedZones || {}).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography color="text.secondary">No zone bundles applied yet (or not reported).</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    Object.entries(appliedZones || {}).map(([cameraId, v]: any) => {
                      const desired = zonesDesiredByCamera[cameraId];
                      const appliedHash = v?.bundle_hash || null;
                      const desiredHash = desired?.bundle_hash || null;
                      const st = driftStatus(desiredHash, appliedHash);
                      return (
                        <TableRow key={cameraId}>
                          <TableCell>{cameraId}</TableCell>
                          <TableCell>{desired ? `v${desired.bundle_version} · ${String(desired.bundle_hash || '').slice(0, 16)}` : '-'}</TableCell>
                          <TableCell>{v ? `v${v.bundle_version} · ${String(v.bundle_hash || '').slice(0, 16)}` : '-'}</TableCell>
                          <TableCell>{statusChip(st)}</TableCell>
                          <TableCell align="right">
                            <Button size="small" onClick={() => republishZones(cameraId)} disabled={republishing || !canWriteBlueprints}>
                              Republish
                            </Button>
                            <Button size="small" onClick={() => onDownloadZones(cameraId)} disabled={!selectedDevice?.device_id} sx={{ ml: 1 }}>
                              Download
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>

            <Divider />

            <Box>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Effective blueprints (edge export)</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={onViewRuntime} disabled={!selectedDevice?.device_id}>
                  View JSON
                </Button>
                <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={onDownloadRuntime} disabled={!selectedDevice?.device_id}>
                  Download JSON
                </Button>
                <Tooltip title="Large payload for deep troubleshooting (includes blueprint content)">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={onDownloadRuntimeDebug}
                      disabled={!selectedDevice?.device_id}
                    >
                      Download debug
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Large payload for deep troubleshooting (includes blueprint content)">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={onViewRuntimeDebug}
                      disabled={!selectedDevice?.device_id}
                    >
                      View debug
                    </Button>
                  </span>
                </Tooltip>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', alignSelf: 'center' }}>
                  cameras={(edgeBlueprints?.blueprints?.cameras || []).length ?? '-'} · main_hash={(edgeBlueprints?.blueprints?.main?.hash || '').slice(0, 24) || '-'}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Runtime blueprints export</DialogTitle>
        <DialogContent>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>{JSON.stringify(viewJson, null, 2).slice(0, 40000)}</pre>
          </Paper>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
            (truncated for display; use “Download JSON” for full export)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
